<script lang="ts" setup>
import { useRoute } from 'vue-router/composables';

import { useProfileStore } from '@/store/profile';

import SiteLogo from '../SiteLogo.vue';
import BaseMenu from '@/components/ui/BaseMenu.vue';

const emit = defineEmits(['resize']);

const tgHelpdeskUrl = import.meta.env.VITE_APP_TELEGRAM_HELPDESK_URL;

let resizeObserver: ResizeObserver | null = null;
let appBarHalfHeight = 0;

const isScrollOverHeader = ref(false);
const headerRef = ref<HTMLElement>();

const profileStore = useProfileStore();
const route = useRoute();

const routerLinkProps = computed(() => {
  if (route.path.includes('account') || profileStore.isGuestMode)
    return { to: '//remoney.tech/', target: '_blank' };

  return { to: { name: 'board' } };
});

function onResize(e: ResizeObserverEntry[]) {
  const contentRect = e[0].contentRect;
  const computedStyle = window.getComputedStyle(e[0].target);

  appBarHalfHeight = Math.floor(contentRect.height / 2);
  emit('resize', { contentRect, computedStyle });
}

function onDocumentScroll() {
  const appBarScrolled = document.documentElement.scrollTop > appBarHalfHeight;

  if (appBarScrolled !== isScrollOverHeader.value) {
    isScrollOverHeader.value = appBarScrolled;
  }
}

if (window.ResizeObserver) {
  resizeObserver = new ResizeObserver(onResize);
}

window.addEventListener('scroll', onDocumentScroll, { passive: true });

onMounted(() => {
  if (resizeObserver) {
    resizeObserver.observe(headerRef.value!);
  }
});

onBeforeUnmount(() => {
  resizeObserver?.disconnect();

  window.removeEventListener('scroll', onDocumentScroll);
});

defineExpose({
  isScrollOverHeader
});
</script>

<template>
  <header
    ref="headerRef"
    :class="{
      [$style['app-bar']]: true,
      [$style['is-scrolled']]: isScrollOverHeader,
    }"
  >
    <div :class="$style.wrapper">
      <router-link v-slot="{ href, navigate }" v-bind="routerLinkProps" custom>
        <a
          :href="href"
          aria-label="Главная"
          title="Главная"
          @click="navigate"
        >
          <SiteLogo size="l" />
        </a>
      </router-link>

      <slot name="center" />

      <slot name="right">
        <BaseMenu :popover-class="$style['support-popover']" disable-teleport :offset="-60" placement="top">
          <template #activator="{ setRef, a11yAttributes, activatorClass }">
            <button :ref="setRef" :class="[$style.support, $style['btn-round'], activatorClass]" v-bind="a11yAttributes" title="Чат поддержки">
              <BaseIcon name="messages2" />
            </button>
          </template>
          <template #popover-content>
            <a :href="tgHelpdeskUrl" target="_blank">
              <BaseBtn variant="round" color="ghost">
                <BaseIcon name="telegram" />
              </BaseBtn>
            </a>
          </template>
        </BaseMenu>
      </slot>
    </div>
  </header>
</template>

<style lang="scss" module>
  @import '@/styles/_media';

  .app-bar {
  position: sticky;
  top: 0;

  height: 72px;

  flex-grow: 0;
  margin-bottom: 60px;

  background-color: transparent;

  z-index: 110;

  transition:
    box-shadow 150ms ease-in-out;

  display: flex;
  justify-content: center;
  align-items: center;
  }

  .is-scrolled {
    box-shadow: 0 1px 24px 0 hsla(228, 16%, 25%, 0.07);
    background-color: var(--app-background-color, #fff);
  }

  .wrapper {
    width: 100%;
    max-width: 1920px;
    padding: 0 24px;

    display: grid;
    grid-template-columns: 1fr auto 1fr;
    align-items: center;

    > a {
      max-width: fit-content;
    }
  }

  .btn-round {
    -webkit-appearance: none;

    height: 60px;
    width: 60px;
    border-radius: 9999px;

    color: var(--electric800);
    background-color: #fff;

    -webkit-box-shadow: 0px 0px 24px 0px #35394912;
    box-shadow: 0px 0px 24px 0px #35394912;

    &:hover {
      background-color: var(--electric10);
    }
    &:active {
      background-color: var(--electric50);
    }
  }

  .support {
    position: fixed;
    bottom: 24px;
    right: 24px;

    //https://stackoverflow.com/questions/20503424/positionfixed-not-working-in-google-chrome
    -webkit-transform: translateZ(0);
  }

  .support-popover {
    border-radius: 9999px;

    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 9px;

    width: 60px;
    padding: 9px;

    &:after {
      content: '';
      height: 51px;
    }
  }

  @include less-desktop {
    .app-bar {
      height: 60px;

      margin-bottom: 42px;
    }
  }

  @include mobile-device {
    .wrapper {
      padding: 0 18px;
    }
  }
</style>
